import { Table } from 'antd'
import type {
  HistoricalDocument,
  ProceduresResponse,
} from 'features/Procedure/state/procedure-api.type'
import { ProcedureTypeCode } from 'features/Procedure/state/procedure.type'
import { useMemo, useState } from 'react'
import { createColumns } from './historical-columns'
import type { HistoricalProcedureTableRow } from './historical-columns'
import styles from './historical-procedures.module.scss'
import { Icon } from '@iconify/react'
import { DocumentPreviewDialog } from './document-preview-dialog'

type Props = {
  historicalData: ProceduresResponse['historical_procedures']
  selectedEncounterUid: string | null
  isLoading: boolean
  selectedPatientId: number | null
}

export const HistoricalProcedures = ({
  historicalData,
  isLoading,
  selectedPatientId,
  selectedEncounterUid,
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(true)
  const [isDocumentDialogOpen, setIsDocumentDialogOpen] = useState(false)
  const [selectedDocument, setSelectedDocument] = useState<HistoricalDocument | null>(null)

  const toggleExpanded = () => {
    setIsExpanded((prev) => !prev)
  }

  const { historicalItems, rowSpanMap } = useMemo(() => {
    if (!historicalData?.items) {
      return { historicalItems: [], rowSpanMap: new Map<string, number>() }
    }

    const flattened: HistoricalProcedureTableRow[] = []
    const dateCountMap = new Map<string, number>()

    historicalData.items.forEach((encounter) => {
      if (encounter?.items?.length > 0 && encounter?.encounter_uuid !== selectedEncounterUid) {
        const cptItems = encounter.items.filter((item) => item.code_type === ProcedureTypeCode.CPT4)

        if (cptItems.length > 0) {
          dateCountMap.set(encounter.encounter_date, cptItems.length + 1)

          flattened.push({
            encounter_date: encounter.encounter_date,
            general_note: encounter.general_note,
            isDescriptionRow: true,
            uniqueKey: `${encounter.encounter_id}-description`,
          })

          cptItems.forEach((item, index) => {
            flattened.push({
              ...item,
              encounter_date: encounter.encounter_date,
              general_note: encounter.general_note,
              uniqueKey: `${encounter.encounter_id}-${item.id}`,
              isFirstInGroup: index === 0,
              isDescriptionRow: false,
            })
          })
        }
      }
    })

    return { historicalItems: flattened, rowSpanMap: dateCountMap }
  }, [historicalData, selectedEncounterUid])

  const columns = useMemo(() => createColumns(rowSpanMap), [rowSpanMap])

  const documents = useMemo(() => {
    return historicalData?.historical_documents || []
  }, [historicalData])

  const handleOpenDocumentDialog = () => {
    if (documents.length > 0) {
      setSelectedDocument(documents[0])
      setIsDocumentDialogOpen(true)
    }
  }

  const handleCloseDocumentDialog = () => {
    setIsDocumentDialogOpen(false)
    setSelectedDocument(null)
  }

  if (!historicalItems?.length && !documents?.length) {
    return null
  }

  return (
    <div className={styles.historicalSection}>
      <div className={styles.header} onClick={toggleExpanded}>
        <h3 className={styles.title}>Previous treatments</h3>
        <Icon
          icon={isExpanded ? 'mdi:chevron-up' : 'mdi:chevron-down'}
          width={24}
          height={24}
          className={styles.chevronIcon}
        />
      </div>
      {isExpanded && (
        <>
          {documents.length > 0 && (
            <button className={styles.addDocumentButton} onClick={handleOpenDocumentDialog}>
              <div className={styles.addDocumentButtonContent}>
                <Icon icon='solar:document-add-bold-duotone' width={32} height={32} />
                Patient history document available
              </div>
              <span className={styles.addDocumentButtonArrow}>View</span>
            </button>
          )}
          {historicalItems.length > 0 && (
            <Table
              rowKey='uniqueKey'
              dataSource={historicalItems}
              columns={columns}
              loading={isLoading}
              tableLayout='auto'
              bordered
              pagination={false}
              className={styles.table}
            />
          )}
        </>
      )}
      <DocumentPreviewDialog
        isOpen={isDocumentDialogOpen}
        selectedPatientId={selectedPatientId}
        selectedEncounterUid={selectedEncounterUid}
        document={selectedDocument}
        onClose={handleCloseDocumentDialog}
      />
    </div>
  )
}
